import { AnalyticsConfig, KoiAnalytics } from './analytics';
import BasePlayer from './base-player';
import { MediaData } from './types';
import './waveform';

class KoiAudioPlayer extends BasePlayer {
  showWaveform?: boolean;
  #handleResize?: () => void;
  private readonly creationTimestamp: number;

  constructor() {
    super();
    this.showWaveform = false;
    this.creationTimestamp = Date.now();
  }

  private _analytics?: KoiAnalytics;

  get analytics(): KoiAnalytics | undefined {
    return this._analytics;
  }

  _mediaElement!: HTMLMediaElement;

  get mediaElement(): HTMLMediaElement | undefined {
    return this._mediaElement;
  }

  _mediaController?: HTMLElement;

  get mediaController(): HTMLElement | undefined {
    return this._mediaController;
  }

  private initializeAnalytics() {
    if (!this.orgId || !this.mediaId) {
      throw new Error('KoiPlayer: orgId and mediaId are required for analytics');
    }

    const config: AnalyticsConfig = {
      mediaElement: this._mediaElement!,
      player_init_time: this.creationTimestamp,
      orgId: this.orgId!,
      env: 'sandbox', //todo
      mediaId: this.mediaId!,
      type: 'audio',
      meta: this.meta,
      debug: true,
    };
    this._analytics = new KoiAnalytics(config);
    this._analytics.init();
  }

  renderPlayer(audioData: MediaData): void {
    this.hideLoadingState();
    if (this._mediaController) {
      const existingAudio = this._mediaController.querySelector('audio');
      if (existingAudio instanceof HTMLAudioElement) {
        existingAudio.setAttribute('src', audioData.mediaUrl);
        return;
      }
      this.unregisterMediaController(this._mediaController);
      this._mediaController.remove();
      this._mediaController = undefined;
    }

    this._mediaController = document.createElement('media-controller');
    this.registerMediaController(this._mediaController);
    this._mediaController.setAttribute('audio', '');
    const container = document.createElement('div');

    const audio = document.createElement('audio');
    audio.setAttribute('slot', 'media');
    audio.setAttribute('src', audioData.mediaUrl);
    this._mediaController.appendChild(audio);

    let waveformElement: HTMLElement | null = null;
    if (audioData.waveform) {
      container.setAttribute('style', 'width: 100%;');
      waveformElement = document.createElement('koi-waveform');
      waveformElement.setAttribute('data', JSON.stringify(audioData.waveform));

      // Pass through waveform CSS variables from host
      const waveformVars = [
        '--waveform-background-color',
        '--waveform-center-line-color',
        '--waveform-played-color',
        '--waveform-unplayed-color',
        '--waveform-bar-thickness',
      ];

      const hostStyles = getComputedStyle(this);
      const waveformStyles: string[] = [];

      waveformVars.forEach(varName => {
        const value = hostStyles.getPropertyValue(varName).trim();
        if (value) {
          waveformStyles.push(`${varName}: ${value}`);
        }
      });

      if (waveformStyles.length > 0) {
        const existingStyle = waveformElement.getAttribute('style') || '';
        waveformElement.setAttribute('style', `${existingStyle}; ${waveformStyles.join('; ')}`);
      }

      container.appendChild(waveformElement);
    }

    const controlBar = document.createElement('media-control-bar');
    const controlElements = this.generateControlElements(this.type!, false, false);
    controlBar.appendChild(controlElements);
    controlBar.setAttribute('style', 'width: 100%;');

    container.appendChild(controlBar);
    this._mediaController.appendChild(container);
    this.shadowRoot.appendChild(this._mediaController);

    const audioElement = this._mediaController.querySelector('audio');
    if (audioElement) {
      this._mediaElement = audioElement;

      // Connect waveform time updates if waveform exists
      if (waveformElement) {
        const updateWaveformTime = () => {
          const waveform = waveformElement as HTMLElement & {
            updateTime?: (currentTime: number, duration: number) => void;
          };
          if (waveform && typeof waveform.updateTime === 'function') {
            waveform.updateTime(audioElement.currentTime, audioElement.duration);
          }
        };

        // Listen for time updates
        audioElement.addEventListener('timeupdate', updateWaveformTime);
        audioElement.addEventListener('loadedmetadata', updateWaveformTime);
        audioElement.addEventListener('durationchange', updateWaveformTime);

        // Listen for waveform seek events
        waveformElement.addEventListener('waveform-seek', (event: Event) => {
          const customEvent = event as CustomEvent<{ targetTime: number; ratio: number }>;
          const { targetTime } = customEvent.detail;

          if (audioElement && !isNaN(targetTime) && targetTime >= 0) {
            audioElement.currentTime = targetTime;
          }
        });
      }
    }

    if (!this.stream) {
      this.initializeAnalytics();
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.type = 'audio';

    if (this.mediaId && this.stream) {
      console.error('KoiPlayer: Both media-id and stream provided. Only one should be used.');
      this.renderError(this.t('errors.bothMediaAndStream'));
      return;
    }

    if (this.mediaId && !this.orgId) {
      console.error('KoiPlayer: No org-id provided');
      this.renderError(this.t('errors.noOrgId'));
      return;
    }

    if (!this.mediaId && !this.stream) {
      console.error('KoiPlayer: No media-id or stream provided');
      this.renderError(this.t('errors.noMediaOrStream'));
      return;
    }

    if (this.mediaId) {
      const minHeight = this.showWaveform ? 220 : 140;
      this.showLoadingState(this.t('loading.audio'), { minHeight });
      this.fetchAudioData({
        mediaId: this.mediaId,
        orgId: this.orgId,
        env: this.env,
        showWaveform: this.showWaveform,
      })
        .then(audioData => {
          this.renderPlayer(audioData);
          this.meta.ready = true;
          this.finalizeSetup(true);
        })
        .catch(error => {
          console.error('KoiPlayer: Error fetching audio data', error);
          this.renderError(this.t('errors.errorLoadingAudio'));
        });
    } else if (this.stream) {
      this.renderPlayer({ mediaUrl: this.stream });
      this.meta.ready = true;
      this.finalizeSetup(true);
    }
  }

  disconnectedCallback(): void {
    if (this.#handleResize) {
      window.removeEventListener('resize', this.#handleResize);
      this.#handleResize = undefined;
    }
  }
}

export default KoiAudioPlayer;
